import { useEffect, useState } from 'react';
import { Download, KeyRound, Loader2 } from 'lucide-react';
import { api } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';

/* =====================================================================
   Custodia del sello · CUMPLIMIENTO F8, y el paquete de salida · F9
   =====================================================================
   Solo el dueño. Ni un byte de la llave privada llega aquí: la API no
   la devuelve por ninguna ruta (hay una prueba que lo intenta).
   ===================================================================== */
export default function CustodiaDelSello() {
  const avisos = useAvisos();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [bajando, setBajando] = useState(false);
  useEffect(() => {
    let vivo = true;
    api.get('/cumplimiento/custodia').then((x) => { if (vivo) setD(x); }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, []);
  if (error) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!d) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Revisando la custodia…
      </p>
    );
  }
  async function paquete() {
    setBajando(true);
    try {
      guardarArchivo(await api.descargar('/cumplimiento/paquete', {}), 'tego-paquete-de-salida.zip');
    } catch (e) { avisos.error(e.message); } finally { setBajando(false); }
  }
  return (
    <div className="flex max-w-4xl flex-col gap-6 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Quién guarda el sello fiscal</h1>
        <p className="text-gray-600">Tus certificados, la llave que los guarda y cada vez que se usaron.</p>
        <Ayuda id="cumplimiento.custodia.pantalla" />
      </header>

      <section className={`rounded-xl border p-4 ${d.llave.propia ? 'border-green-200 bg-green-50' : 'border-red-300 bg-red-50'}`}>
        <h2 className="flex items-center gap-2 font-semibold text-gray-900"><KeyRound className="h-4 w-4" aria-hidden="true" /> Llave maestra {d.llave.propia ? 'propia' : 'DERIVADA'} · huella {d.llave.huella}</h2>
        <p className="text-sm text-gray-800">{d.llave.dice}</p>
        <Ayuda id="cumplimiento.custodia.llave" />
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Certificados</h2>
        {d.certificados.length === 0 ? <p className="text-sm text-gray-600">No has subido ningún sello. Se sube en Facturación → Emisor.</p> : (
          <ul className="mt-2 divide-y divide-gray-100 text-sm">
            {d.certificados.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 py-2">
                <span className="font-mono">{c.numero}</span>
                <span className="text-gray-600">{c.rfc}</span>
                <span className="text-gray-500">vence {String(c.vigente_hasta).slice(0, 10)}</span>
                <span className={`rounded px-2 text-xs ${c.retirado_en ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-800'}`}>{c.retirado_en ? `retirado: ${c.motivo_retiro || ''}` : 'activo'}</span>
                {!c.se_puede_abrir && !c.retirado_en && <span className="text-xs text-red-700">cerrado con otra llave: no se puede abrir</span>}
                <span className="ml-auto text-gray-600">{c.usos} usos{c.ultimo_uso ? ` · último ${String(c.ultimo_uso).slice(0, 16).replace('T', ' ')}` : ''}</span>
              </li>))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Cada vez que se abrió la llave</h2>
        {d.usos.length === 0 ? <p className="text-sm text-gray-600">Todavía ninguna. Cada factura, complemento o global sellado deja aquí una línea.</p> : (
          <ul className="mt-2 text-sm text-gray-700">{d.usos.map((u) => <li key={u.id}>{String(u.creado_en).slice(0, 16).replace('T', ' ')} · {u.proposito} · {u.quien || 'sistema'}{u.referencia ? ` · ${u.referencia}` : ''}</li>)}</ul>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Llevarte todo</h2>
        <p className="text-sm text-gray-600">Todo lo de tu empresa, con los CFDI como archivos, la bitácora y un índice con la huella de cada uno.</p>
        <Ayuda id="cumplimiento.paquete.boton" />
        <button type="button" onClick={paquete} disabled={bajando}
                className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">
          {bajando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />} Paquete de salida
        </button>
      </section>
    </div>
  );
}
